import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { de } from '@/i18n/de';
import { useAppStore } from '@/lib/store';
import { wsClient } from '@/lib/wsClient';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/Button';
import { RulesSheet } from '@/components/RulesSheet';

interface HomeScreenProps {
  onCreate: () => void;
  onJoin: (roomId: string) => void;
  onLocal: () => void;
  onRejoin: () => void;
}

export function HomeScreen({ onCreate, onJoin, onLocal, onRejoin }: HomeScreenProps) {
  const rooms = useAppStore((s) => s.lobbyRooms);
  const status = useAppStore((s) => s.connectionStatus);
  const [rulesOpen, setRulesOpen] = useState(false);

  useEffect(() => {
    if (status !== 'connected') return;
    wsClient.send({ type: 'lobby:subscribe' });
    return () => wsClient.send({ type: 'lobby:unsubscribe' });
  }, [status]);

  return (
    <AppShell>
      <div className="mx-auto flex max-w-xl flex-col gap-8 py-6 sm:py-10">
        <div className="text-center">
          <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
            {de.app.title}
          </h1>
          <p className="mt-2 text-fg-muted">{de.app.tagline}</p>
        </div>

        <div className="mx-auto flex w-full max-w-sm flex-col gap-3">
          <Button onClick={onCreate}>{de.home.createRoom}</Button>
          <Button variant="secondary" onClick={onLocal}>
            {de.home.localMode}
          </Button>
          <p className="text-center text-sm text-fg-faint">{de.home.localModeHint}</p>
          <div className="mt-1 flex justify-center gap-4 text-sm text-fg-muted">
            <button
              type="button"
              onClick={onRejoin}
              className="underline underline-offset-2 hover:text-fg"
            >
              {de.rejoin.cta}
            </button>
            <button
              type="button"
              onClick={() => setRulesOpen(true)}
              className="underline underline-offset-2 hover:text-fg"
            >
              {de.rules.open}
            </button>
          </div>
        </div>

        <div>
          <h2 className="mb-3 font-display text-lg font-semibold text-fg-muted">
            {de.home.openRooms}
          </h2>

          {/* Der ConnectionBanner blendet `connecting` bewusst aus — ohne diesen
              Zweig läse der erste Start sich als „kein Raum offen", obwohl die
              Liste schlicht noch nicht da ist. */}
          {status === 'connecting' ? (
            <ul className="flex flex-col gap-3" aria-busy="true" aria-label={de.home.loadingRooms}>
              {[0, 1].map((index) => (
                <li
                  key={index}
                  className="h-[4.5rem] animate-pulse-soft rounded-2xl border border-surface-border bg-surface"
                />
              ))}
            </ul>
          ) : rooms.length === 0 ? (
            <p className="rounded-xl border border-dashed border-surface-border px-4 py-8 text-center text-sm text-fg-faint">
              {de.home.noRooms}
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {rooms.map((room) => (
                <motion.li
                  layout
                  key={room.roomId}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-surface-border bg-surface px-4 py-3.5"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 truncate font-display font-semibold">
                      {room.roomName}
                      {room.locked && <span aria-label={de.home.locked}>🔒</span>}
                    </div>
                    <div className="text-sm text-fg-muted">
                      {de.home.players(room.playerCount, room.maxPlayers)} ·{' '}
                      {de.home.mafiaCount(room.mafiaCount)}
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    className="min-h-11 shrink-0 px-4 text-sm"
                    disabled={room.locked}
                    onClick={() => onJoin(room.roomId)}
                  >
                    {de.home.join}
                  </Button>
                </motion.li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <RulesSheet open={rulesOpen} onClose={() => setRulesOpen(false)} />
    </AppShell>
  );
}
